/**
 * useContainerPresetCatalog — named container presets from the admin
 * system_platforms catalog (v2.20.0).
 *
 * Source: system_platforms/{platformId}/formats/{formatId}.assetSlots[].
 * Only slots with an explicit pixelSize {width, height} become selectable
 * container presets (aspectRatio-only slots are hints for a future
 * ratio+dimension mode and are NOT listed). A platform is the future
 * "slot pack" unit — presetRef ("platformId/formatId[/slotKey]") keeps the
 * containerSize traceable back to its pack.
 *
 * Loading is lazy (enabled flag), cached for the session, and failure-safe:
 * an unreachable catalog yields an empty list so the editor falls back to
 * its built-in presets.
 */

import { useEffect, useState } from 'react';
import { systemPlatformService } from '@modules/content-plan/services/SystemPlatformService';

export interface ContainerCatalogPreset {
  /** "platformId/formatId" or "platformId/formatId/slotKey" */
  presetRef: string;
  /** Display label, e.g. "Feed Post — square (1080×1080)" */
  label: string;
  platformId: string;
  platformName: string;
  width: number;
  height: number;
}

let catalogCache: ContainerCatalogPreset[] | null = null;
let catalogPromise: Promise<ContainerCatalogPreset[]> | null = null;

function slotKeyOf(slot: Record<string, unknown>, index: number): string | null {
  for (const key of ['slotKey', 'key', 'id', 'name']) {
    const value = slot[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return index > 0 ? String(index) : null; // single/first unnamed slot → format-level ref
}

async function loadCatalog(): Promise<ContainerCatalogPreset[]> {
  const platforms = await systemPlatformService.listPlatforms({ activeOnly: true });
  const presets: ContainerCatalogPreset[] = [];
  for (const platform of platforms) {
    let formats;
    try {
      formats = await systemPlatformService.listFormats(platform.platformId);
    } catch {
      continue; // one unreadable platform must not sink the whole catalog
    }
    for (const format of formats) {
      if (format.isActive === false) continue;
      (format.assetSlots ?? []).forEach((slot, index) => {
        const pixelSize = (slot as { pixelSize?: { width?: unknown; height?: unknown } }).pixelSize;
        const width = Number(pixelSize?.width);
        const height = Number(pixelSize?.height);
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
          return; // aspectRatio-only or malformed slots are not selectable
        }
        const slotKey = slotKeyOf(slot, index);
        const formatName = String(format.name ?? format.id);
        presets.push({
          presetRef: slotKey
            ? `${platform.platformId}/${format.id}/${slotKey}`
            : `${platform.platformId}/${format.id}`,
          label: `${formatName}${slotKey && slotKey !== formatName ? ` — ${slotKey}` : ''} (${Math.round(width)}×${Math.round(height)})`,
          platformId: platform.platformId,
          platformName: platform.displayName,
          width: Math.round(width),
          height: Math.round(height),
        });
      });
    }
  }
  return presets;
}

export function useContainerPresetCatalog(enabled: boolean): ContainerCatalogPreset[] {
  const [catalog, setCatalog] = useState<ContainerCatalogPreset[]>(catalogCache ?? []);

  useEffect(() => {
    if (!enabled || catalogCache) return;
    let cancelled = false;
    if (!catalogPromise) {
      catalogPromise = loadCatalog().catch(() => {
        catalogPromise = null; // allow a retry on the next mount
        return [] as ContainerCatalogPreset[];
      });
    }
    catalogPromise.then((presets) => {
      if (presets.length > 0) catalogCache = presets;
      if (!cancelled) setCatalog(presets);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return catalog;
}
